import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import "./i18n/config";
import "./index.css";
import { registerSW } from "virtual:pwa-register";
import { LogLevel } from "@grafana/faro-web-sdk";

import { getFaro, initFaro } from "@/observability/faro";
import { isChunkLoadError, reloadOnceForStaleChunks } from "@/lib/chunk-reload";
import App from "./app";

initFaro();

window.addEventListener("unhandledrejection", (event) => {
    if (isChunkLoadError(event.reason)) {
        event.preventDefault();
        reloadOnceForStaleChunks();
    }
});

window.addEventListener("error", (event) => {
    if (isChunkLoadError(event.error) || isChunkLoadError(event.message)) {
        reloadOnceForStaleChunks();
    }
});

registerSW({
    immediate: true,
    onRegisterError: (error) => {
        // Service-worker registration fails on restricted browsers (private
        // mode, disabled storage) and during the asset swap of a deploy. The
        // app works fine without it, so log instead of surfacing an exception.
        getFaro()?.api.pushLog(
            [`Service worker registration failed: ${String(error)}`],
            { level: LogLevel.WARN },
        );
    },
});

const queryClient = new QueryClient();

const baseUrl =
    document.getElementsByTagName("base")[0]?.getAttribute("href") ?? "/";
const container =
    document.getElementById("root") ?? document.createElement("div");
const root = createRoot(container);

root.render(
    <React.StrictMode>
        <QueryClientProvider client={queryClient}>
            <BrowserRouter basename={baseUrl}>
                <App />
            </BrowserRouter>
        </QueryClientProvider>
    </React.StrictMode>,
);
